import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API } from '../App';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { NumericInput } from '../components/ui/numeric-input';
import { Card } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { Fuel, Building2, Users, MapPin } from 'lucide-react';

const dealerships = ['BPCL', 'HPCL', 'IOCL', 'Jio-BP', 'Nayara', 'Reliance Petroleum', 'Shell'];

function SetupPump() {
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    pump_name: '',
    owner_name: '',
    dealership: '',
    owner_2_name: '',
    owner_3_name: '',
    owner_whatsapp: '',
    owner_2_whatsapp: '',
    owner_3_whatsapp: '',
    location: '',
    town: '',
    district: '',
    state: '',
    mobile: '',
    email: '',
    hsd_dispensers: 2,
    ms_dispensers: 2,
    hsd_tanks: 1,
    ms_tanks: 1,
    hsd_tank_capacity: 10000,
    ms_tank_capacity: 10000
  });

  const handleChange = (field, value) => {
    setFormData({ ...formData, [field]: value });
  };

  const handleSubmit = async () => {
    setLoading(true);
    try {
      const res = await axios.post(`${API}/auth/setup-pump`, formData);
      toast.success(`Pump setup successful! Your temporary PIN is: ${res.data.temp_pin}`);
      setTimeout(() => navigate('/login'), 3000);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Setup failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background p-4 md:p-8">
      <div className="max-w-4xl mx-auto">
        <div className="text-center mb-8">
          <div className="flex justify-center mb-4">
            <div className="w-20 h-20 bg-primary rounded-md flex items-center justify-center">
              <Fuel className="w-12 h-12 text-white" />
            </div>
          </div>
          <h1 className="text-4xl font-heading font-bold mb-2" data-testid="setup-heading">Setup Your Pump Station</h1>
          <p className="text-muted">Complete the setup to get started</p>
        </div>

        <div className="mb-8">
          <div className="flex justify-between items-center">
            {[1, 2, 3].map((s) => (
              <React.Fragment key={s}>
                <div className={`flex items-center justify-center w-12 h-12 rounded-full ${
                  step >= s ? 'bg-primary text-white' : 'bg-secondary text-muted'
                } font-bold`}>
                  {s}
                </div>
                {s < 3 && <div className={`flex-1 h-1 mx-2 ${
                  step > s ? 'bg-primary' : 'bg-secondary'
                }`} />}
              </React.Fragment>
            ))}
          </div>
          <div className="flex justify-between mt-2 text-sm">
            <span>Basic Info</span>
            <span>Location</span>
            <span>Infrastructure</span>
          </div>
        </div>

        <Card className="p-8 border border-border">
          {step === 1 && (
            <div className="space-y-6">
              <div className="flex items-center gap-3 mb-6">
                <Building2 className="w-6 h-6 text-primary" />
                <h2 className="text-2xl font-heading font-bold">Basic Information</h2>
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium mb-2">Pump Name *</label>
                  <Input
                    data-testid="pump-name-input"
                    value={formData.pump_name}
                    onChange={(e) => handleChange('pump_name', e.target.value)}
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">Owner Name *</label>
                  <Input
                    data-testid="owner-name-input"
                    value={formData.owner_name}
                    onChange={(e) => handleChange('owner_name', e.target.value)}
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">Fuel Dealership *</label>
                  <Select value={formData.dealership} onValueChange={(value) => handleChange('dealership', value)}>
                    <SelectTrigger data-testid="dealership-select">
                      <SelectValue placeholder="Select dealership" />
                    </SelectTrigger>
                    <SelectContent>
                      {dealerships.map(d => <SelectItem key={d} value={d}>{d}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">Owner 2 Name</label>
                  <Input
                    value={formData.owner_2_name}
                    onChange={(e) => handleChange('owner_2_name', e.target.value)}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">Owner 3 Name</label>
                  <Input
                    value={formData.owner_3_name}
                    onChange={(e) => handleChange('owner_3_name', e.target.value)}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">Owner WhatsApp</label>
                  <Input
                    data-testid="owner-whatsapp-input"
                    type="tel"
                    value={formData.owner_whatsapp}
                    onChange={(e) => handleChange('owner_whatsapp', e.target.value)}
                    placeholder="+91 XXXXXXXXXX"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">Owner 2 WhatsApp</label>
                  <Input
                    type="tel"
                    value={formData.owner_2_whatsapp}
                    onChange={(e) => handleChange('owner_2_whatsapp', e.target.value)}
                    placeholder="+91 XXXXXXXXXX"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">Owner 3 WhatsApp</label>
                  <Input
                    type="tel"
                    value={formData.owner_3_whatsapp}
                    onChange={(e) => handleChange('owner_3_whatsapp', e.target.value)}
                    placeholder="+91 XXXXXXXXXX"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">Mobile *</label>
                  <Input
                    data-testid="mobile-input"
                    type="tel"
                    value={formData.mobile}
                    onChange={(e) => handleChange('mobile', e.target.value)}
                    required
                  />
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium mb-2">Email *</label>
                  <Input
                    data-testid="email-input"
                    type="email"
                    value={formData.email}
                    onChange={(e) => handleChange('email', e.target.value)}
                    required
                  />
                </div>
              </div>
            </div>
          )}

          {step === 2 && (
            <div className="space-y-6">
              <div className="flex items-center gap-3 mb-6">
                <MapPin className="w-6 h-6 text-primary" />
                <h2 className="text-2xl font-heading font-bold">Location Details</h2>
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium mb-2">Location/Address *</label>
                  <Input
                    data-testid="location-input"
                    value={formData.location}
                    onChange={(e) => handleChange('location', e.target.value)}
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">Town *</label>
                  <Input
                    value={formData.town}
                    onChange={(e) => handleChange('town', e.target.value)}
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">District *</label>
                  <Input
                    value={formData.district}
                    onChange={(e) => handleChange('district', e.target.value)}
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">State *</label>
                  <Input
                    value={formData.state}
                    onChange={(e) => handleChange('state', e.target.value)}
                    required
                  />
                </div>
              </div>
            </div>
          )}

          {step === 3 && (
            <div className="space-y-6">
              <div className="flex items-center gap-3 mb-6">
                <Fuel className="w-6 h-6 text-primary" />
                <h2 className="text-2xl font-heading font-bold">Infrastructure Setup</h2>
              </div>

              <div className="space-y-8">
                <div>
                  <h3 className="text-lg font-semibold mb-4 text-petrol">MS (Petrol)</h3>
                  <div className="grid md:grid-cols-3 gap-6">
                    <div>
                      <label className="block text-sm font-medium mb-2">Number of Dispensers *</label>
                      <NumericInput
                        data-testid="ms-dispensers-input"
                        min="1"
                        value={formData.ms_dispensers}
                        onChange={(e) => handleChange('ms_dispensers', parseInt(e.target.value) || 0)}
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-2">Number of Tanks *</label>
                      <NumericInput
                        min="1"
                        value={formData.ms_tanks}
                        onChange={(e) => handleChange('ms_tanks', parseInt(e.target.value) || 0)}
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-2">Tank Capacity (L) *</label>
                      <NumericInput
                        min="1"
                        value={formData.ms_tank_capacity}
                        onChange={(e) => handleChange('ms_tank_capacity', parseFloat(e.target.value) || 0)}
                        required
                      />
                    </div>
                  </div>
                </div>

                <div>
                  <h3 className="text-lg font-semibold mb-4 text-diesel">HSD (Diesel)</h3>
                  <div className="grid md:grid-cols-3 gap-6">
                    <div>
                      <label className="block text-sm font-medium mb-2">Number of Dispensers *</label>
                      <NumericInput
                        data-testid="hsd-dispensers-input"
                        min="1"
                        value={formData.hsd_dispensers}
                        onChange={(e) => handleChange('hsd_dispensers', parseInt(e.target.value) || 0)}
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-2">Number of Tanks *</label>
                      <NumericInput
                        min="1"
                        value={formData.hsd_tanks}
                        onChange={(e) => handleChange('hsd_tanks', parseInt(e.target.value) || 0)}
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-2">Tank Capacity (L) *</label>
                      <NumericInput
                        min="1"
                        value={formData.hsd_tank_capacity}
                        onChange={(e) => handleChange('hsd_tank_capacity', parseFloat(e.target.value) || 0)}
                        required
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          <div className="flex justify-between mt-8">
            <Button
              variant="outline"
              onClick={() => setStep(Math.max(1, step - 1))}
              disabled={step === 1}
            >
              Previous
            </Button>
            
            {step < 3 ? (
              <Button onClick={() => setStep(step + 1)}>
                Next
              </Button>
            ) : (
              <Button 
                data-testid="setup-submit-button"
                onClick={handleSubmit} 
                disabled={loading}
              >
                {loading ? 'Setting up...' : 'Complete Setup'}
              </Button>
            )}
          </div>
        </Card>

        <div className="mt-6 text-center">
          <p className="text-sm text-muted">
            Already have an account?{' '}
            <a href="/login" className="text-accent hover:underline font-medium">Sign In</a>
          </p>
        </div>
      </div>
    </div>
  );
}

export default SetupPump;